import React from "react";

import {
  TIME_FIELD,
  TIME_ERROR_MESSAGE,
  EMPTY_STRING,
} from "../../../constants";
import CustomError from "../../../shared/components/custom-error/CustomError";
import TimeInput from "../../../shared/components/time-input/TimeInput";

const ERROR = "Error";
const { START, END } = TIME_FIELD;
const { START_INVALID, START_RANGE, END_RANGE, END_INVALID } =
  TIME_ERROR_MESSAGE;

const CompanyOfficeHoursForm = ({
  schedule,
  setSchedule,
  isEditing = true,
  hideHeading = false,
  errors = {},
  trigger = () => {},
}) => {
  const handleTimeChange = (index, field, e) => {
    const inputTime = e.target.value;
    // Update the time for the specific day at the given index
    const updatedSchedule = [...schedule];
    updatedSchedule[index][field] = inputTime;
    // Validate time format when the input is complete
    if (inputTime.length === 5) {
      const isStartField = field === START;
      const isEndField = field === END;

      if (isValidTimeRange(inputTime)) {
        updatedSchedule[index][`${START}${ERROR}`] = null;
        updatedSchedule[index][`${END}${ERROR}`] = null;

        const isTimeValid = isValidTime(
          updatedSchedule[index].startTime,
          updatedSchedule[index].endTime,
          field
        );

        if (!isTimeValid) {
          updatedSchedule[index][`${field}${ERROR}`] = isStartField
            ? START_INVALID
            : isEndField
            ? END_INVALID
            : null;
        }
      } else {
        updatedSchedule[index][`${field}${ERROR}`] = isStartField
          ? START_RANGE
          : isEndField
          ? END_RANGE
          : null;
      }
    }
    setSchedule(updatedSchedule);
  };

  // Function to validate if the time entered is valid (00:00 - 23:59)
  const isValidTimeRange = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  const handleCheckboxChange = (index) => {
    // Toggle 'enabled' for the day at the given index
    const updatedSchedule = [...schedule];
    updatedSchedule[index].isActive = !updatedSchedule[index].isActive;
    setSchedule(updatedSchedule);
    trigger("officeHours");
  };

  const isValidTime = (startTime, endTime, type) => {
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    // Create dates with the same date for comparison
    const startDate = new Date(1970, 0, 1, startHour, startMinute);
    const endDate = new Date(1970, 0, 1, endHour, endMinute);

    if (type === START) {
      return startDate.getTime() < endDate.getTime();
    }

    // endTime is after startTime
    return endDate.getTime() > startDate.getTime();
  };

  return (
    <div className="company-office-hours">
      {!hideHeading && (
        <div className="row">
          <div className="col-12 company-office-hours-heading">
            Company Office Hours
          </div>
          {errors?.officeHours && (
            <div className="error text-center">
              {errors?.officeHours?.message}
            </div>
          )}
        </div>
      )}
      <div className="row">
        <div
          className={`col-12 col-xl-6 pt-xl-3 pb-xl-2 pe-xl-1 ${
            isEditing || schedule?.filter((day) => day.isActive).length > 4
              ? "schedule-col-1"
              : EMPTY_STRING
          }`}
        >
          {(isEditing ? schedule : schedule?.filter((day) => day.isActive))?.map(
            (day, index) =>
              index < 4 && (
                <div
                  className={`row mb-3 align-items-center ${
                    index === 3 ? "mb-xl-0" : ""
                  }`}
                  key={day.id}
                >
                  <div className="col-4 mb-2 mb-sm-0 schedule-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={day.isActive}
                        onChange={() => handleCheckboxChange(index)}
                        disabled={!isEditing}
                      />
                      {day.name}
                    </label>
                  </div>
                  <div className="col-8 text-xl-end">
                    <span className="p-1 m-1">
                      <TimeInput
                        disabled={!day.isActive || !isEditing}
                        value={day.startTime}
                        handleTimeChange={(e) =>
                          handleTimeChange(index, TIME_FIELD.START, e)
                        }
                      />
                    </span>
                    <span className="p-1 m-1">
                      <TimeInput
                        disabled={!day.isActive || !isEditing}
                        value={day.endTime}
                        handleTimeChange={(e) =>
                          handleTimeChange(index, TIME_FIELD.END, e)
                        }
                      />
                    </span>
                  </div>
                  {/* Display validation message */}
                  {isEditing && (
                    <>
                      <CustomError errorMessage={day?.startTimeError} />
                      <CustomError errorMessage={day?.endTimeError} />
                    </>
                  )}
                </div>
              )
          )}
        </div>
        <div className="col-12 col-xl-6 pt-xl-3 pb-xl-2 schedule-col-2">
          {(isEditing ? schedule : schedule?.filter((day) => day.isActive))?.map(
            (day, index) =>
              index > 3 && (
                <div className="row mb-3 align-items-center" key={day.id}>
                  <div className="col-4 mb-2 mb-sm-0 schedule-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={day.isActive}
                        onChange={() => handleCheckboxChange(index)}
                        disabled={!isEditing}
                      />
                      {day.name}
                    </label>
                  </div>
                  <div className="col-8">
                    <span className="p-1 m-1">
                      <TimeInput
                        disabled={!day.isActive || !isEditing}
                        value={day.startTime}
                        handleTimeChange={(e) =>
                          handleTimeChange(index, TIME_FIELD.START, e)
                        }
                      />
                    </span>
                    <span className="p-1 m-1">
                      <TimeInput
                        disabled={!day.isActive || !isEditing}
                        value={day.endTime}
                        handleTimeChange={(e) =>
                          handleTimeChange(index, TIME_FIELD.END, e)
                        }
                      />
                    </span>
                  </div>
                  {/* Display validation message */}
                  <CustomError errorMessage={day?.startTimeError} />
                  <CustomError errorMessage={day?.endTimeError} />
                </div>
              )
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyOfficeHoursForm;
